import cx from 'classnames';
import {map} from 'lodash';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Icon, IconName, ImageWithLoader} from 'components';
import {UserSessionContext} from 'context';
import {VirtualBackgroundType} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {buildTranslationKey} from 'utils/transform';

import {SidebarView} from '../../types';
import {SidebarContent} from '../SidebarContent';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.VIRTUAL_BACKGROUND_SIDEBAR;

type BackgroundEffect = {
    name: string;
    icon: IconName;
    type: VirtualBackgroundType
}

export const VirtualBackgroundSidebar: React.FC = () => {
    const {t} = useTranslation();
    const {
        virtualBackgroundConfig, virtualBackgroundContent, sidebarView, updateVirtualBackground
    } = useContext(UserSessionContext);

    const [initialized, setInitialized] = useState(false);

    const backgroundEffects: BackgroundEffect[] = useMemo(() => [
        {
            name: 'none',
            icon: 'x-circle',
            type: VirtualBackgroundType.None
        },
        {
            name: 'background_blur',
            icon: 'background-blur',
            type: VirtualBackgroundType.Blur
        },
    ], []);

    const handleEffectClick = useCallback((effectType: VirtualBackgroundType) => {
        if (virtualBackgroundConfig?.type !== effectType) {
            updateVirtualBackground({type: effectType, image: null});
        }
    }, [virtualBackgroundConfig?.type, updateVirtualBackground]);

    const handleImageClick = useCallback((path: string) => {
        if (virtualBackgroundConfig?.image !== path) {
            updateVirtualBackground({type: VirtualBackgroundType.Image, image: path});
        }
    }, [virtualBackgroundConfig, updateVirtualBackground]);

    useEffect(() => {
        if (sidebarView === SidebarView.VirtualBackground && !initialized) {
            setInitialized(true);
        }
    }, [initialized, sidebarView]);

    if (sidebarView !== SidebarView.VirtualBackground && !initialized) {
        return null;
    }

    return (
        <SidebarContent view={SidebarView.VirtualBackground} header={<div className='tw-ts-h5'>{t(T_KEYS.TITLE)}</div>}>
            <div className='tw-mb-4'>
                <div className='tw-ts-h5 tw-my-2'>{t(T_KEYS.SECTIONS.EFFECTS)}</div>
                <div className='tw-flex tw-flex-wrap'>
                    {map(backgroundEffects, (effect) => (
                        <div
                            key={effect.name}
                            className='tw-m-2 tw-w-32 tw-cursor-pointer'
                            onClick={() => handleEffectClick(effect.type)}
                        >
                            <div className={cx('tw-w-full tw-h-32 tw-flex-center tw-border tw-border-default tw-border-dashed', {
                                'tw-border-primary-light': virtualBackgroundConfig?.type === effect.type
                            })}
                            >
                                <Icon color='default-dimmed' name={effect.icon} size='2xl' />
                            </div>
                            <div className='tw-mt-2'>{t(T_KEYS.EFFECTS[buildTranslationKey(effect.name)])}</div>
                        </div>
                    ))}
                </div>
            </div>
            <div className='tw-mb-4'>
                <div className='tw-ts-h5 tw-my-2'>{t(T_KEYS.SECTIONS.BACKGROUND)}</div>
                <div className='tw-flex tw-flex-wrap'>
                    {map(virtualBackgroundContent?.files, ({path}) => (
                        <div
                            key={path}
                            className='tw-m-2 tw-w-32 tw-cursor-pointer'
                            onClick={() => handleImageClick(path)}
                        >
                            <div className={
                                cx('tw-w-full tw-overflow-hidden tw-relative',
                                    'tw-h-32 tw-flex-center tw-border tw-border-default tw-border-dashed', {
                                        'tw-border-primary-light': virtualBackgroundConfig?.image === path
                                    })
                            }
                            >
                                <ImageWithLoader path={path} alt={path} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </SidebarContent>
    );
};